import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import PropTypes from 'prop-types'

// Internal components
import Button from '../Button/Button'
import CalendarBlueprint from '../CalendarBlueprint/CalendarBlueprint'
import CalendarMenu from '../CalendarMenu/CalendarMenu'
import Rating from '../Rating/Rating'

// Utils
import { reorderDayLabels } from '../../utils/calendar/calendar'
import getCombinedExercisesData from '../../utils/data/getCombinedExercisesData'
import CalendarMatchInfo from '../CalendarMatchInfo/CalendarMatchInfo'
import FormationBlock from '../FormationBlock/FormationBlock'
import { UserContext } from '../../context/UserContext'

const CalendarCard = (props) => {
  const {
    id,
    isTrainingDay,
    isMatchDay,
    event,
    date,
    type,
    blueprint,
    rating,
    past,
    preview,
    isLastOfMonth,
    sameDay,
    isWithinBlueprint
  } = props

  const userContext = useContext(UserContext)
  // Translations
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT
  )

  // States
  const [cardEvents, setCardEvents] = useState([])
  const [eventCached] = useState(event)
  const [loadedState, setLoadedState] = useState(false)

  const dayLabelData = reorderDayLabels()
  const dateObject = new Date(date)
  const dateNumber = dateObject.getDate()
  const dateLabel = t(dayLabelData[dateObject.getDay()])?.substring(0, 3)
  const cardRef = useRef()

  useEffect(() => {
    setTimeout(() => setLoadedState(true), 1000)
  }, [])

  useEffect(() => {
    setCardEvents(getCombinedExercisesData(event))
    if (event !== eventCached) cardRef?.current?.classList.remove('is-outofview')
  }, [event])

  useEffect(() => {
    ScrollTrigger.create({
      trigger: cardRef?.current,
      onEnter: () => cardRef?.current?.classList.remove('is-outofview'),
      onEnterBack: () => cardRef?.current?.classList.remove('is-outofview'),
      start: 'top bottom-=150'
    })
  }, [loadedState])

  const view = 'calendar'

  const trainingMenu = [
    {
      label: t('BTN_CALENDAR_MENU_COPY_TRAINING'),
      modal: {
        type: 'api',
        route: 'copy/training-to-date',
        id: event?.id || 0
      }
    },
    {
      label: t('BTN_CALENDAR_MENU_REMOVE_TRAINING'),
      modal: {
        type: 'api',
        route: blueprint?.id ? 'delete/training-from-blueprint' : 'delete/training-from-calendar',
        id: id
      }
    }
  ]

  const matchMenu =
    event?.matchStatus === 'start'
      ? [
          {
            label: t('BTN_CALENDAR_MENU_EDIT_MATCH'),
            modal: {
              type: 'api',
              route: 'edit/match',
              id: event?.id || 0
            }
          },
          {
            label: t('BTN_CALENDAR_MENU_REMOVE_MATCH'),
            modal: {
              type: 'api',
              route: 'delete/match',
              id: event?.id || 0
            }
          }
        ]
      : [
          {
            label: t('BTN_CALENDAR_MENU_REMOVE_MATCH'),
            modal: {
              type: 'api',
              route: 'delete/match',
              id: event?.id || 0
            }
          }
        ]

  return (
    <div
      className={`CalendarCard${
        blueprint?.id ? ` CalendarCard--blueprint CalendarCard--blueprint${blueprint?.section}` : ''
      }${(isMatchDay || type === 'match') && isWithinBlueprint ? ` CalendarCard--blueprintmatch` : ''}${
        isLastOfMonth ? ' is-lastofmonth' : ''
      } is-outofview`}
      card-date={`${date}`}
      data-type={type}
      ref={cardRef}
    >
      {blueprint?.id && <CalendarBlueprint {...blueprint} cardRef={cardRef} preview={preview} />}
      {(isMatchDay || type === 'match') && isWithinBlueprint && (
        <CalendarBlueprint {...blueprint} cardRef={cardRef} preview={preview} />
      )}
      {dateNumber && dateLabel && (
        <div className="CalendarCard-date">
          {!sameDay && (
            <>
              <span className="CalendarCard-dateNumber">{String(dateNumber)}</span>
              <span className="CalendarCard-dateLabel">{dateLabel}</span>
            </>
          )}
        </div>
      )}
      <div className="CalendarCard-block">
        <div className="CalendarCard-content">
          {!preview && event?.id && (
            <CalendarMenu
              menu={type === 'training' ? trainingMenu : matchMenu}
              modifiers={['Calendar-menu--trainingcard']}
              cardRef={cardRef}
            />
          )}
          {event?.id ? (
            <>
              <Link href={type === 'training' ? `/training/id/${event.id}` : `/match/id/${event.id}`}>
                <a className={`CalendarCard-link ${type === 'match' ? 'CalendarCard-link--match' : ''}`}>
                  {type === 'training' && (
                    <>
                      {event?.objectives && <h2 className="CalendarCard-category">{event.objectives[0].title}</h2>}
                      {event?.title && <h3 className="CalendarCard-title">{event.title}</h3>}
                      {cardEvents && (
                        <ul className="CalendarCard-list">
                          {cardEvents.map((exercise, index) => {
                            return (
                              <li className="CalendarCard-listItem" key={`CalendarCard-${index}`}>
                                {exercise?.title}
                              </li>
                            )
                          })}
                        </ul>
                      )}
                    </>
                  )}
                  {type === 'match' && (
                    <>
                      <div className="CalendarCard-matchInfo">
                        <CalendarMatchInfo event={event} />
                      </div>
                      {view === 'calendar' && event?.formation && (
                        <div className="CalendarCard-matchFormation">
                          <FormationBlock players={event.currentLineUp} formation={event.formation} />
                        </div>
                      )}
                    </>
                  )}
                </a>
              </Link>
              {past && type === 'training' && (
                <div className="CalendarCard-ratingBar">
                  {rating.length && (
                    <div className="CalendarCard-rating">
                      <Rating rating={rating[0]} />
                    </div>
                  )}
                  <Button
                    label={
                      rating[0].average && rating[0].average !== 0
                        ? t('CALENDAR_CARD__BTN_EVALUATION')
                        : t('CALENDAR_CARD__BTN_EVALUATE')
                    }
                    icon={{ id: 'icon-chevron-right' }}
                    modifiers={['Button--calendarCardRating']}
                    modal={{
                      type: 'api',
                      route: `${
                        rating[0].average && rating[0].average !== 0 ? 'get/calendar-rating' : 'add/rating-to-training'
                      }`,
                      id: id
                    }}
                  />
                </div>
              )}
            </>
          ) : (
            !past && (
              <>
                {isTrainingDay && (
                  <Button
                    label={`${t('CALENDAR_CARD__BTN_ADDTRAINING')}${
                      blueprint?.objective?.title
                        ? `<em>${
                            blueprint?.objective?.title !== 'Mixed'
                              ? blueprint?.objective?.title
                              : t('CALENDAR_CARD__BTN_VARIED')
                          }</em>`
                        : ''
                    }`}
                    icon={{ id: 'icon-add' }}
                    modal={{ type: 'api', route: 'add/training-to-calendar', id: date }}
                    modifiers={['Button--calendarCardItem']}
                  />
                )}
                {isMatchDay && (
                  <Button
                    label={`${t('CALENDAR_CARD__BTN_ADDMATCH')}${
                      blueprint?.objective?.title
                        ? `<em>${
                            blueprint?.objective?.title !== 'Mixed'
                              ? blueprint?.objective?.title
                              : t('CALENDAR_CARD__BTN_VARIED')
                          }</em>`
                        : ''
                    }`}
                    icon={{ id: 'icon-add' }}
                    modal={{
                      type: 'api',
                      route: `create/match/${date}`
                    }}
                    modifiers={['Button--calendarCardItem', 'Button--calendarCardItemMatchmode']}
                  />
                )}
              </>
            )
          )}
        </div>
      </div>
    </div>
  )
}

CalendarCard.defaultProps = {
  isTrainingDay: false,
  isMatchDay: false,
  sameDay: false
}

CalendarCard.propTypes = {
  id: PropTypes.any,
  isTrainingDay: PropTypes.bool,
  isMatchDay: PropTypes.bool,
  sameDay: PropTypes.bool,
  event: PropTypes.any,
  type: PropTypes.string,
  date: PropTypes.string,
  past: PropTypes.bool,
  blueprint: PropTypes.any,
  rating: PropTypes.any,
  preview: PropTypes.bool,
  isLastOfMonth: PropTypes.bool,
  isWithinBlueprint: PropTypes.bool
}

export default CalendarCard
